import {Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import styles from './performance.module.scss';

/**
 * The one way this report explains a term: a small (i) beside it that opens
 * a plain-language tooltip. Keyboard-focusable, so the explanation is not
 * mouse-only.
 */
export const Info = ({text}) => (
  <Tooltip title={text} overlayClassName={styles.infoTooltip}>
    <InfoCircleOutlined
      className={styles.infoIcon}
      tabIndex={0}
      aria-label={typeof text === 'string' ? text : 'More information'}
    />
  </Tooltip>
);

/** A label with its explanation, for headings and table column titles. */
export const Explained = ({children, text}) => (
  <span className={styles.explained}>
    {children}
    <Info text={text} />
  </span>
);

/**
 * What every column, card and legend entry means, in one place so the same
 * term is explained the same way wherever it appears.
 */
export const TERMS = {
  opened: 'Tickets customers started during the selected dates.',
  resolved:
    'Tickets marked resolved during the selected dates. Some may have been opened earlier.',
  abandoned:
    'Tickets the customer left without a resolution. Counted separately — never as resolved.',
  pending:
    'Tickets still open right now — neither resolved nor abandoned. This is live and does not change with the dates.',
  agentPending:
    'Tickets currently active with this agent (HUMAN_ACTIVE). Live — not tied to the dates.',
  unassigned:
    'Tickets waiting in the support queue that no agent has picked up yet.',
  rating:
    'The average of the stars (1–5) customers gave. Tickets nobody rated are left out, so check how many ratings it is based on.',
  coverage:
    'The share of resolved tickets the customer actually rated. Low coverage means the average rests on few opinions.',
  turnaround:
    'How long tickets sat with the support team: from reaching the human queue until resolved. Plain clock time — nights and weekends count.',
  customerWait:
    'Calendar resolution time: from the customer opening the ticket until it was resolved. Plain clock time — nights and weekends count.',
  firstResponse:
    'From the ticket reaching the human queue to the first reply a support agent sent the customer. AI replies and internal notes don’t count. Clock time, measured once per ticket, and credited to whoever sent that reply.',
  unanswered:
    'Tickets with the support team that no agent has replied to yet, and how many of those have already waited longer than the target. Live — not tied to the dates.',
  median:
    'The middle value: half the tickets were faster, half slower. Unlike the average, one very slow ticket barely moves it.',
  ratingCount: 'How many resolved tickets were rated by the customer.',
  share:
    'Tickets opened for this topic, and their share of all tickets opened in the period (not just the ten shown).',
  shareResolved:
    'Tickets resolved for this topic, and their share of all tickets resolved in the period (not just the ten shown).',
  change:
    'Compared with the same number of days just before the selected period. "New" means there were none then.',
  rankingRating:
    'Rating of this topic’s resolved tickets — which can be fewer than the tickets opened.',
  unattributed:
    'Tickets resolved without a credited agent: by Sassandra (AI), an FAQ article, or the customer closing it themselves.',
  total:
    'The whole team, worked out by the server from every ticket — not by adding up or averaging the rows above.',
};

/** Who closed a resolved ticket, explained for the breakdown legend. */
export const CLOSER_TERMS = {
  customerNoHelp:
    'The customer closed the ticket themselves, without help from Sassandra, an FAQ or an agent.',
  ai: 'Sassandra, the AI assistant, resolved it.',
  faq: 'The customer found the answer in an FAQ article.',
  human: 'A support agent resolved it.',
};
